import React, { useRef } from "react";
import PropTypes from "prop-types";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaGooglePlay,
  FaApple,
  FaArrowRight,
  FaInfoCircle,
  FaTag,
  FaHeart,
  FaSeedling,
} from "react-icons/fa";
import "./HeroSection.css";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 14 },
  },
};
const imageShowcaseVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};
const layerVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};
const titleContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
};
const titleCharVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", damping: 15, stiffness: 400 },
  },
};

const HeroSection = ({
  title,
  subtitle,
  descriptionpart1,
  descriptionpart2,
  primaryCta,
  secondaryCta,
  tertiaryCta,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  onTertiaryCtaClick,
  primaryCtaIcon,
  secondaryCtaIcon,
  tertiaryCtaIcon,
  imageUrl,
  showAppBadges,
  imageAlt,
}) => {
  const titleChars = Array.from(title);
  const showcaseRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: showcaseRef,
    offset: ["start end", "end start"],
  });

  const showcaseY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const orbShopY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const orbGiveY = useTransform(scrollYProgress, [0, 1], ["0%", "-50%"]);
  const orbGrowY = useTransform(scrollYProgress, [0, 1], ["-5%", "35%"]);

  return (
    <section className="showcase-hero-container">
      <motion.div
        className="showcase-hero-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          className="showcase-hero-title"
          variants={titleContainerVariants}
        >
          {titleChars.map((char, index) => (
            <motion.span key={index} variants={titleCharVariants}>
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
          <motion.span
            className="showcase-hero-subtitle"
            variants={itemVariants}
          >
            {subtitle}
          </motion.span>
        </motion.h1>
        <motion.p className="showcase-hero-description" variants={itemVariants}>
          {descriptionpart1}
          <span className="showcase-hero-description-highlight">
            {descriptionpart2}
          </span>
        </motion.p>
        <motion.div className="showcase-hero-cta-group" variants={itemVariants}>
          {primaryCta && (
            <button
              className="showcase-cta-button primary"
              onClick={onPrimaryCtaClick}
            >
              {secondaryCtaIcon} <span>{secondaryCta}</span>
            </button>
          )}
          {secondaryCta && (
            <button
              className="showcase-cta-button secondary"
              onClick={onSecondaryCtaClick}
            >
              {tertiaryCtaIcon} <span>{tertiaryCta}</span>
            </button>
          )}
        </motion.div>
        {/* {tertiaryCta && (
          <motion.div
            className="showcase-hero-tertiary-cta"
            variants={itemVariants}
          >
            
            <a href="#" onClick={onTertiaryCtaClick}>
              
              <span>{tertiaryCta}</span> {tertiaryCtaIcon}
            </a>
          </motion.div>
        )} */}
        {showAppBadges && (
          <motion.div
            className="showcase-app-badges-container"
            variants={itemVariants}
          >
            <a
              href="#"
              className="showcase-app-badge"
              aria-label="Get it on Google Play"
              onClick={onTertiaryCtaClick}
            >
              <FaGooglePlay className="showcase-app-badge-icon" />
              <div className="showcase-app-badge-text">
                <span>GET IT ON</span> <p>Google Play</p>
              </div>
            </a>
            {/* <a
              href="#"
              className="showcase-app-badge"
              aria-label="Download on the App Store"
            >
              
              <FaApple className="showcase-app-badge-icon" />
              <div className="showcase-app-badge-text">
                
                <span>Download on the</span> <p>App Store</p>
              </div>
            </a> */}
          </motion.div>
        )}
      </motion.div>
      <div className="showcase-hero-visuals">
        <motion.div
          ref={showcaseRef}
          className="showcase-composition"
          variants={imageShowcaseVariants}
          initial="hidden"
          animate="visible"
          style={{ y: showcaseY }}
        >
          <motion.div
            className="showcase-layer-base"
            variants={layerVariants}
          ></motion.div>
          <motion.div
            className="showcase-floating-orb orb-shop"
            variants={layerVariants}
            style={{ y: orbShopY }}
          >
            <FaTag className="orb-icon" />
          </motion.div>
          <motion.div
            className="showcase-layer-phone"
            variants={layerVariants}
            whileHover={{
              scale: 1.03,
              rotate: 0,
              transition: { type: "spring", stiffness: 300 },
            }}
          >
            <img src={imageUrl} alt={imageAlt} />
          </motion.div>
          <motion.div
            className="showcase-floating-orb orb-give"
            variants={layerVariants}
            style={{ y: orbGiveY }}
          >
            <FaHeart className="orb-icon" />
          </motion.div>
          <motion.div
            className="showcase-floating-orb orb-grow"
            variants={layerVariants}
            style={{ y: orbGrowY }}
          >
            <FaSeedling className="orb-icon" />
          </motion.div>
          <motion.div
            className="showcase-layer-accent accent-1"
            variants={layerVariants}
          ></motion.div>
          <motion.div
            className="showcase-layer-accent accent-2"
            variants={layerVariants}
          ></motion.div>
        </motion.div>
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  descriptionpart1: PropTypes.string.isRequired,
  descriptionpart2: PropTypes.string,
  primaryCta: PropTypes.string,
  secondaryCta: PropTypes.string,
  tertiaryCta: PropTypes.string,
  onPrimaryCtaClick: PropTypes.func,
  onSecondaryCtaClick: PropTypes.func,
  onTertiaryCtaClick: PropTypes.func,
  primaryCtaIcon: PropTypes.node,
  secondaryCtaIcon: PropTypes.node,
  tertiaryCtaIcon: PropTypes.node,
  imageUrl: PropTypes.string.isRequired,
  showAppBadges: PropTypes.bool,
  imageAlt: PropTypes.string.isRequired,
};
HeroSection.defaultProps = {
  subtitle: "",
  primaryCta: null,
  secondaryCta: null,
  tertiaryCta: null,
  onPrimaryCtaClick: () => {},
  onSecondaryCtaClick: () => {},
  onTertiaryCtaClick: () => {},
  primaryCtaIcon: <FaArrowRight />,
  secondaryCtaIcon: <FaInfoCircle />,
  tertiaryCtaIcon: <FaArrowRight />,
  showAppBadges: true,
};

export default HeroSection;
